/**
 * Edit Profile Modal Component
 * Modal for editing user name and phone number
 * @component EditProfileModal
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { translate } from '../../locales';
import { updateUserProfile } from '../../services/authService';
import './style.css';
import Tooltip from '../../components/Tooltip';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const t = (key: any) => translate(key, language as 'en' | 'ua');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Функція для скидання форми до початкових значень
  const resetForm = () => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setPhoneNumber(user.phone || '');
      setEmail(user.email || '');
    }
    setError(null);
  };

  useEffect(() => {
    if (user) {
      resetForm();
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName.trim() || !lastName.trim()) {
      setError('Ім\'я та прізвище обов\'язкові');
      return;
    }

    // Перевіряємо, що принаймні один контакт заповнений
    if (!email.trim() && !phoneNumber.trim()) {
      setError('Принаймні одне поле (email або телефон) має бути заповнене');
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);

      // Підготовляємо дані для оновлення
      const updateData: any = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      };

      // Додаємо email якщо він заповнений
      if (email.trim()) {
        updateData.email = email.trim();
      }

      // Додаємо телефон якщо він заповнений
      if (phoneNumber.trim()) {
        updateData.phone = phoneNumber.trim();
      }

      // Викликаємо API для оновлення профілю
      const result = await updateUserProfile(updateData);

      // Оновлюємо локальні дані користувача
      if (result.user) {
        updateUser(result.user);
        
        // Оновлюємо поля форми новими даними
        setFirstName(result.user.first_name || '');
        setLastName(result.user.last_name || '');
        setPhoneNumber(result.user.phone || '');
        setEmail(result.user.email || '');
      }

      console.log('✅ Профіль успішно оновлено:', result);
      onClose();
    } catch (error: any) {
      console.error('❌ Помилка оновлення профілю:', error);
      setError(error.message || 'Помилка оновлення профілю');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      // Скидаємо форму до початкових значень
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className="bi bi-pencil-square"></i>
            {t('profile.edit.title')}
          </h2>
          <button 
            className="modal-close-btn" 
            onClick={handleClose}
            disabled={isUpdating}
            aria-label="Закрити"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

                <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-grid">
            {/* Перший рядок: Ім'я та Прізвище */}
            <div className="form-group group-firstName">
              <label htmlFor="firstName">
                <i className="bi bi-person"></i>
                Ім'я *
              </label>
              <Tooltip content="Введіть ваше ім'я" position="top" align="end" variant="arrow">
                <input
                  id="firstName"
                  type="text"
                  className="form-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Введіть ім'я"
                  disabled={isUpdating}
                  required
                  autoComplete="given-name"
                />
              </Tooltip>
            </div>

            <div className="form-group group-lastName">
              <label htmlFor="lastName">
                <i className="bi bi-person"></i>
                Прізвище *
              </label>
              <Tooltip content="Введіть ваше прізвище" position="top" align="end" variant="arrow">
                <input
                  id="lastName"
                  type="text"
                  className="form-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Введіть прізвище"
                  disabled={isUpdating}
                  required
                  autoComplete="family-name"
                />
              </Tooltip>
            </div>

            {/* Другий рядок: Email та Телефон */}
            <div className="form-group group-email">
              <label htmlFor="email">
                <i className="bi bi-envelope"></i>
                {t('profile.fields.email')}
                {user?.login_type === 'email' && ' (основний)'}
              </label>
              <Tooltip content={
                user?.login_type === 'email' 
                  ? 'Основний email можна змінювати'
                  : 'Можна додати або змінити email'
              } position="top" align="end" variant="arrow">
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isUpdating}
                  placeholder={user?.login_type === 'phone' ? 'Додайте email' : 'Змініть email'}
                  autoComplete="email"
                />
              </Tooltip>
            </div>

            <div className="form-group group-phoneNumber">
              <label htmlFor="phoneNumber">
                <i className="bi bi-telephone"></i>
                {t('profile.fields.phone')}
                {user?.login_type === 'phone' && ' (основний)'}
              </label>
              <Tooltip content={
                user?.login_type === 'phone' 
                  ? 'Основний номер можна змінювати'
                  : 'Можна додати або змінити телефон'
              } position="top" align="end" variant="arrow">
                <input
                  id="phoneNumber"
                  type="tel"
                  className="form-input"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('profile.edit.phonePlaceholder') || 'Введіть номер телефону'}
                  disabled={isUpdating}
                  autoComplete="tel"
                />
              </Tooltip>
            </div>

            {/* Третій рядок: Роль та Тип бізнесу */}
            <div className="form-group group-role">
              <label htmlFor="role">
                <i className="bi bi-shield-check"></i>
                {t('profile.fields.role')}
              </label>
              <Tooltip content={t('profile.edit.roleDisabled')} position="top" align="end" variant="arrow">
                <input
                  id="role"
                  type="text"
                  className="form-input"
                  value={user?.role || ''}
                  disabled
                />
              </Tooltip>
            </div>

            <div className="form-group group-businessType">
              <label htmlFor="businessType">
                <i className="bi bi-briefcase"></i>
                {t('profile.fields.businessType')}
              </label>
              <Tooltip content={t('profile.edit.businessTypeDisabled')} position="top" align="end" variant="arrow">
                <input
                  id="businessType"
                  type="text"
                  className="form-input"
                  value={user?.businessType || 'Не вказано'}
                  disabled
                />
              </Tooltip>
            </div>

            {error && (
              <div className="error-message">
                <i className="bi bi-exclamation-circle"></i>
                {error}
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isUpdating}
            >
              <i className="bi bi-x-circle"></i>
              {t('profile.edit.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isUpdating}
            >
              <i className="bi bi-check-circle"></i>
              {isUpdating ? t('profile.edit.saving') : t('profile.edit.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
